import { defineMessages, FormattedMessage, useIntl } from '@edx/frontend-platform/i18n';
import dayjs from 'dayjs';

import {
  useCheckoutIntent,
  useFirstBillableInvoice,
  usePurchaseSummaryPricing,
} from '@/components/app/data';
import { LONG_MONTH_DATE_FORMAT, SUBSCRIPTION_TRIAL_LENGTH_DAYS } from '@/components/app/data/constants';
import { isEssentialsFlow } from '@/components/app/routes/loaders/utils';
import { DisplayPrice } from '@/components/DisplayPrice';
import { ExternalLink } from '@/components/ExternalLink';
import { FieldContainer } from '@/components/FieldContainer';
import EVENT_NAMES from '@/constants/events';
import { sendEnterpriseCheckoutTrackingEvent } from '@/utils/common';

const freeTrialDateRangeText = (
  { startTime, endTime }: { startTime: FirstBillableInvoice['startTime'], endTime: FirstBillableInvoice['endTime'] },
) => `${dayjs(startTime).format(LONG_MONTH_DATE_FORMAT)} - ${dayjs(endTime).format(LONG_MONTH_DATE_FORMAT)}`;

const messages = defineMessages({
  subscriptionManagement: {
    id: 'checkout.freeTrialSubscriptionStartMessage.subscriptionManagement',
    defaultMessage: 'Subscription Management',
    description: 'Link text for the subscription management page',
  },
});

export const SubscriptionStartMessage = () => {
  const intl = useIntl();
  const isEssentials = isEssentialsFlow();
  const { data: firstBillableInvoice, isLoading } = useFirstBillableInvoice();
  const { data: checkoutIntent } = useCheckoutIntent();
  const { yearlySubscriptionCostForQuantity } = usePurchaseSummaryPricing();
  const subscriptionManagementUrl = checkoutIntent?.adminPortalUrl
    ? new URL('admin/subscriptions/manage-learners/', `${checkoutIntent.adminPortalUrl.replace(/\/$/, '')}/`).toString()
    : null;

  if (isLoading || !firstBillableInvoice) {
    return null;
  }

  const {
    startTime = null,
    endTime = null,
    hasStartAndEndTime = false,
  } = firstBillableInvoice;

  if (!hasStartAndEndTime) {
    return null;
  }

  return (
    <FieldContainer>
      <div>
        <h3>
          <FormattedMessage
            id="checkout.freeTrialSubscriptionStartMessage.title"
            defaultMessage="Your free {trialDays}-day trial for {productName} subscription has started."
            description="Title for the free trial success field section"
            values={{
              trialDays: SUBSCRIPTION_TRIAL_LENGTH_DAYS,
              productName: isEssentials ? 'edX Essentials' : 'edX Team',
            }}
          />
        </h3>
        <p className="fs-4 font-weight-light">
          <FormattedMessage
            id="checkout.freeTrialSubscriptionStartMessage.description"
            defaultMessage="Your trial expires on {boldDate}. When your free trial ends, your subscription will
            begin, and we will charge your payment method on file {price} USD per year. To avoid being charged,
            you must cancel before your trial expires. This subscription will automatically renew every year unless
            you cancel from the {link} page."
            description="Description text explaining the subscription details"
            values={{
              boldDate: <span className="font-weight-bold">{dayjs(endTime).format(LONG_MONTH_DATE_FORMAT)}</span>,
              link: (
                subscriptionManagementUrl
                  ? (
                    <ExternalLink
                      href={subscriptionManagementUrl}
                      onClick={() => sendEnterpriseCheckoutTrackingEvent({
                        checkoutIntentId: checkoutIntent?.id ?? null,
                        checkoutIntentUuid: checkoutIntent?.uuid ?? null,
                        eventName: EVENT_NAMES.SUBSCRIPTION_CHECKOUT.SUBSCRIPTION_MANAGEMENT_LINK_CLICKED,
                        properties: {
                          adminPortalUrl: subscriptionManagementUrl,
                        },
                      })}
                    >
                      {intl.formatMessage(messages.subscriptionManagement)}
                    </ExternalLink>
                  )
                  : (
                    <span>{intl.formatMessage(messages.subscriptionManagement)}</span>
                  )
              ),
              price: (<DisplayPrice value={yearlySubscriptionCostForQuantity ?? 0} />),
            }}
          />
        </p>
      </div>
      <div className="mt-3">
        <h4>
          <FormattedMessage
            id="checkout.freeTrialSubscriptionStartMessage.sub.title"
            defaultMessage="Your subscription plan"
            description="Sub title for the free trial success field section"
          />
        </h4>
        <p className="fs-4 font-weight-light">
          {freeTrialDateRangeText({ startTime: startTime!, endTime: endTime! })}
        </p>
      </div>
    </FieldContainer>
  );
};

export default SubscriptionStartMessage;
